import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Loader2, Rocket, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { useCreateCheckout } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";

const plans = [
  {
    name: "Trial",
    price: "R$ 0",
    period: "",
    description: "Comece a testar agora!",
    highlight: "7 dias de uso grátis",
    features: [
      "Acesso completo por 7 dias",
      "Clientes e projetos ilimitados",
      "Timer e relatórios completos",
      "Sem cartão de crédito",
    ],
    cta: "Começar meu trial",
    popular: false,
    microcopy: "Sem compromisso • Cancele quando quiser",
    planId: null as null,
  },
  {
    name: "Pro Mensal",
    price: "R$ 49,90",
    period: "/mês",
    description: "Para freelancers profissionais",
    features: [
      "Clientes ilimitados",
      "Projetos ilimitados",
      "Relatórios completos",
      "Exportação PDF/CSV",
      "Suporte prioritário",
    ],
    cta: "Assinar Pro",
    popular: true,
    microcopy: "Cancele quando quiser",
    planId: "mensal" as const,
  },
  {
    name: "Pro Anual",
    price: "R$ 399,90",
    period: "/ano",
    description: "Economia de 2 meses",
    features: [
      "Tudo do Pro Mensal",
      "Pagamento único anual",
      "Equivale a R$ 33,33/mês",
      "33% de desconto",
    ],
    cta: "Assinar Anual",
    popular: false,
    bestValue: true,
    microcopy: "Cancele quando quiser",
    planId: "anual" as const,
  },
];

export function PricingSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const createCheckout = useCreateCheckout();
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleSubscribe = async (planId: "mensal" | "anual" | null) => {
    if (!planId) {
      // Trial plan - just redirect to signup
      navigate("/signup");
      return;
    }

    if (!user) {
      // Not logged in - redirect to signup
      navigate("/signup");
      return;
    }

    setLoadingPlan(planId);
    try {
      const { url } = await createCheckout.mutateAsync(planId);
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível iniciar o checkout. Tente novamente.",
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section className="relative py-16 sm:py-20 overflow-x-hidden bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wide text-foreground mb-4">
            COMECE GRÁTIS.{" "}
            <span className="text-primary">
              EVOLUA QUANDO PRECISAR.
            </span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Sem surpresas. Sem taxa escondida. Você escolhe o que faz sentido.
          </p>
        </div>
        <div className="mt-16 grid gap-6 md:grid-cols-3 lg:max-w-5xl lg:mx-auto">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`relative bg-card border ${
                plan.popular
                  ? "border-primary shadow-lg shadow-primary/10 md:scale-105"
                  : "border-border"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground border-0 shadow-md">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Mais Popular
                  </Badge>
                </div>
              )}
              {"bestValue" in plan && plan.bestValue && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge variant="secondary" className="bg-muted text-foreground border-border">
                    Melhor Custo-Benefício
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center">
                <CardTitle className="text-2xl text-foreground">{plan.name}</CardTitle>
                <CardDescription className="text-muted-foreground">{plan.description}</CardDescription>
                {"highlight" in plan && plan.highlight && (
                  <p className="text-primary font-semibold text-sm mt-2">{plan.highlight}</p>
                )}
                <div className="mt-4">
                  <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                  {plan.period && (
                    <span className="text-muted-foreground">{plan.period}</span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                {plan.planId ? (
                  <Button
                    className={`w-full ${
                      plan.popular
                        ? "bg-primary hover:bg-primary/90 text-primary-foreground shadow-md"
                        : ""
                    }`}
                    variant={plan.popular ? "default" : "outline"}
                    onClick={() => handleSubscribe(plan.planId)}
                    disabled={loadingPlan === plan.planId}
                  >
                    {loadingPlan === plan.planId ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      plan.cta
                    )}
                  </Button>
                ) : (
                  <Link to="/signup" className="w-full">
                    <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                      <Rocket className="h-4 w-4" />
                      {plan.cta}
                    </Button>
                  </Link>
                )}
                {plan.microcopy && (
                  <p className="text-xs text-muted-foreground text-center">
                    {plan.microcopy}
                  </p>
                )}
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
